import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { recipientsApi, documentsApi, getErrorMessage } from '../services/api';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Search, 
  RefreshCw, 
  User,
  Building2,
  Mail,
  Clock,
  CheckCircle2,
  Bell,
  ChevronDown,
  ChevronUp,
  FileText,
  Eye,
  XCircle,
  Shield,
  FileCheck,
  AlertCircle,
  X,
  Ban,
  Trash2,
  Send,
  KeyRound
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Bullhorn entity colors (with safe fallback so unexpected entity types
// like 'placement' don't crash the row renderer)
const ENTITY_COLORS = {
  candidate: { bg: 'bg-[#4CAF50]', text: 'text-white', label: 'Candidate', lightBg: 'bg-[#4CAF50]/10', borderColor: 'border-[#4CAF50]', iconColor: 'text-[#4CAF50]' },
  contact: { bg: 'bg-[#F9A825]', text: 'text-white', label: 'Contact', lightBg: 'bg-[#F9A825]/10', borderColor: 'border-[#F9A825]', iconColor: 'text-[#F9A825]' },
  placement: { bg: 'bg-[#1a4a5e]', text: 'text-white', label: 'Placement', lightBg: 'bg-[#1a4a5e]/10', borderColor: 'border-[#1a4a5e]', iconColor: 'text-[#1a4a5e]' },
};
const FALLBACK_ENTITY_COLOR = { bg: 'bg-slate-400', text: 'text-white', label: 'Recipient', lightBg: 'bg-slate-100', borderColor: 'border-slate-300', iconColor: 'text-slate-600' };

// Status badge component
const StatusBadge = ({ status, size = 'sm' }) => {
  const config = {
    sent: { class: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Sent', icon: Clock },
    opened: { class: 'bg-cyan-100 text-cyan-700 border-cyan-200', label: 'Opened', icon: Eye },
    in_progress: { class: 'bg-purple-100 text-purple-700 border-purple-200', label: 'In Progress', icon: RefreshCw },
    completed: { class: 'bg-green-100 text-green-700 border-green-200', label: 'Completed', icon: CheckCircle2 },
    pending_approval: { class: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Pending Approval', icon: FileCheck },
    approved: { class: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Approved', icon: Shield },
    expired: { class: 'bg-red-100 text-red-700 border-red-200', label: 'Expired', icon: XCircle },
  };
  
  const { class: className, label, icon: Icon } = config[status] || config.sent;
  const sizeClass = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm';
  
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-medium border ${className} ${sizeClass}`}>
      <Icon className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} />
      {label}
    </span>
  );
};

// Progress indicator component
const ProgressIndicator = ({ completed, total, label }) => {
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  let colorClass = 'bg-red-500';
  if (percentage >= 100) colorClass = 'bg-emerald-500';
  else if (percentage >= 50) colorClass = 'bg-amber-500';
  else if (percentage > 0) colorClass = 'bg-orange-500';

  return (
    <div className="flex items-center gap-3">
      <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${colorClass}`} style={{ width: `${percentage}%` }} />
      </div>
      <span className="text-sm font-medium min-w-[60px]">{completed}/{total}</span>
    </div>
  );
};

// Expandable row for document details
const RecipientDocumentsPanel = ({ recipient, onSendReminder, sendingReminder, onDocumentCancelled }) => {
  const [cancellingDoc, setCancellingDoc] = useState(null);
  
  const outstandingDocs = recipient.documents?.filter(d => !['completed', 'approved', 'cancelled'].includes(d.status)) || [];
  const completedDocs = recipient.documents?.filter(d => ['completed', 'approved'].includes(d.status)) || [];

  const handleCancelDocument = async (docId, templateName) => {
    if (!window.confirm(`Cancel "${templateName}" for ${recipient.name}? This will remove it from their portal.`)) {
      return;
    }
    
    setCancellingDoc(docId);
    try {
      await documentsApi.cancel(docId);
      toast.success('Document cancelled');
      onDocumentCancelled?.();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to cancel document'));
    } finally {
      setCancellingDoc(null);
    }
  };

  const handlePreview = (doc) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const pdfUrl = doc.completed_document_url
      ? `${baseUrl}${doc.completed_document_url}`
      : doc.template_file_url
        ? `${baseUrl}${doc.template_file_url}`
        : null;
    if (pdfUrl) {
      setPreviewDoc({ ...doc, _pdfUrl: pdfUrl });
    } else {
      toast.info('No PDF available for preview');
    }
  };

  const [previewDoc, setPreviewDoc] = useState(null);

  return (
    <div className="bg-slate-50 border-t border-border p-6 animate-in slide-in-from-top-2 duration-200">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Outstanding Documents */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <h4 className="font-semibold text-amber-900">Outstanding ({outstandingDocs.length})</h4>
          </div>
          {outstandingDocs.length > 0 ? (
            <div className="space-y-2">
              {outstandingDocs.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200 shadow-sm">
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-amber-600" />
                    <div>
                      <p className="font-medium text-sm">{doc.template_name}</p>
                      <p className="text-xs text-muted-foreground">
                        Sent {doc.sent_at ? format(new Date(doc.sent_at), 'MMM d, yyyy') : 'N/A'}
                        {doc.reminder_count > 0 && <span className="text-amber-600"> • {doc.reminder_count} reminder(s)</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={doc.status} />
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-slate-500 hover:text-primary hover:bg-primary/10"
                      onClick={() => handlePreview(doc)}
                      title="Preview document"
                      data-testid={`preview-doc-${doc.id}`}
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleCancelDocument(doc.id, doc.template_name)}
                      disabled={cancellingDoc === doc.id}
                      title="Cancel document"
                    >
                      {cancellingDoc === doc.id ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <X className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic p-3 bg-white rounded-lg border border-dashed">No outstanding documents</p>
          )}
        </div>

        {/* Completed Documents */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <h4 className="font-semibold text-emerald-900">Completed ({completedDocs.length})</h4>
          </div>
          {completedDocs.length > 0 ? (
            <div className="space-y-2">
              {completedDocs.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-emerald-200 shadow-sm">
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-emerald-600" />
                    <div>
                      <p className="font-medium text-sm">{doc.template_name}</p>
                      <p className="text-xs text-muted-foreground">
                        Completed {doc.completed_at ? format(new Date(doc.completed_at), 'MMM d, yyyy') : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-slate-500 hover:text-primary hover:bg-primary/10"
                      onClick={() => handlePreview(doc)}
                      title="Preview document"
                      data-testid={`preview-completed-${doc.id}`}
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    <StatusBadge status={doc.status} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic p-3 bg-white rounded-lg border border-dashed">No completed documents yet</p>
          )}
        </div>
      </div>

      {/* Send Reminder Action */}
      {outstandingDocs.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <Button 
            onClick={() => onSendReminder(recipient)}
            disabled={sendingReminder === recipient.email}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {sendingReminder === recipient.email ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Bell className="w-4 h-4 mr-2" />
            )}
            Send Reminder for {outstandingDocs.length} Outstanding Document{outstandingDocs.length > 1 ? 's' : ''}
          </Button>
        </div>
      )}

      {/* Document Preview Modal */}
      <Dialog open={!!previewDoc} onOpenChange={(open) => { if (!open) setPreviewDoc(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0" aria-describedby="recipient-preview-desc">
          <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
            <DialogTitle className="text-lg font-bold">{previewDoc?.template_name}</DialogTitle>
            <p className="text-sm text-muted-foreground" id="recipient-preview-desc">
              {recipient.name} • {recipient.email}
            </p>
          </DialogHeader>
          <div className="flex-1 overflow-auto bg-slate-100">
            {previewDoc?._pdfUrl && (
              <iframe
                src={previewDoc._pdfUrl}
                title="Document Preview"
                className="w-full border-0"
                style={{ height: 'calc(85vh - 80px)', minHeight: '500px' }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function RecipientsPage({ embedded = false }) {
  const navigate = useNavigate();
  const { getTenantPath, tenant } = useAuth();
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedRow, setExpandedRow] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [resendingLogin, setResendingLogin] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadRecipients();
  }, [entityFilter, statusFilter, page]);

  const loadRecipients = async () => {
    setLoading(true);
    try {
      const response = await recipientsApi.list({
        search: search || undefined,
        entity_type: entityFilter !== 'all' ? entityFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        page,
        limit: 25
      });
      setRecipients(response.data.recipients || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalCount(response.data.total_count || response.data.recipients?.length || 0);
    } catch (error) {
      toast.error('Failed to load recipients');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    setExpandedRow(null);
    loadRecipients();
  };

  const handleClearSearch = () => {
    setSearch('');
    setPage(1);
    setExpandedRow(null);
    loadRecipients();
  };

  const handleSendReminder = async (recipient) => {
    setSendingReminder(recipient.email);
    try {
      await recipientsApi.sendReminder({
        recipient_email: recipient.email
      });
      toast.success(`Reminder sent to ${recipient.name}`);
      loadRecipients();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send reminder'));
    } finally {
      setSendingReminder(null);
    }
  };

  const handleResendLogin = async (recipient) => {
    setResendingLogin(recipient.email);
    try {
      await axios.post(`${API_URL}/api/portal/auth/resend-credentials`, {
        email: recipient.email,
        tenant_id: tenant?.id,
      });
      toast.success(`New login details sent to ${recipient.email}`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to resend login details'));
    } finally {
      setResendingLogin(null);
    }
  };

  // Cache of detailed recipient data (documents array) keyed by email
  const [recipientDetails, setRecipientDetails] = useState({});

  const toggleRow = async (email) => {
    const next = expandedRow === email ? null : email;
    setExpandedRow(next);
    // When expanding, fetch the per-recipient document list (the listing
    // aggregation only returns counts, not the docs themselves).
    if (next && !recipientDetails[email]) {
      try {
        const { data } = await recipientsApi.get(email);
        setRecipientDetails((prev) => ({
          ...prev,
          [email]: [
            ...(data.outstanding_documents || []),
            ...(data.completed_documents || []),
          ],
        }));
      } catch (err) {
        // Silent fail — panel just shows "no documents"; toast would be too noisy
      }
    }
  };

  // Calculate summary stats from current page (using the API's actual field names)
  const completeCount = recipients.filter(
    (r) => (r.pending_documents || 0) === 0 && (r.total_documents || 0) > 0
  ).length;
  const pendingCount = recipients.filter((r) => (r.pending_documents || 0) > 0).length;

  const content = (
    <div className="space-y-6" data-testid="recipients-page">
      {/* Header - only show when not embedded */}
      {!embedded && (
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold">Recipients</h1>
            <p className="text-muted-foreground">Track document progress for each candidate and contact</p>
          </div>
          
          {/* Summary Stats */}
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => navigate(getTenantPath('/send'))} 
              className="btn-primary"
              data-testid="send-document-from-recipients"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Document
            </Button>
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 border border-slate-200">
              <User className="w-4 h-4 text-slate-600" />
              <span className="text-sm font-semibold text-slate-700">{totalCount} Total</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-semibold text-emerald-700">{completeCount} Complete</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-50 border border-amber-200">
              <Clock className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-700">{pendingCount} Pending</span>
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats when embedded */}
      {embedded && (
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 border border-slate-200">
              <User className="w-4 h-4 text-slate-600" />
              <span className="text-sm font-semibold text-slate-700">{totalCount} Total</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-semibold text-emerald-700">{completeCount} Complete</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200">
              <Clock className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-700">{pendingCount} Pending</span>
            </div>
          </div>
          <Button 
            onClick={() => navigate(getTenantPath('/send'))} 
            className="btn-primary"
            data-testid="send-document-from-recipients"
          >
            <Send className="w-4 h-4 mr-2" />
            Send Document
          </Button>
        </div>
      )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 p-4 bg-white rounded-xl border border-border shadow-sm">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by name or email..."
              className="pl-10 pr-10"
              data-testid="search-input"
            />
            {search && (
              <button 
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Select value={entityFilter} onValueChange={(v) => { setEntityFilter(v); setPage(1); setExpandedRow(null); }}>
            <SelectTrigger className="w-[160px]" data-testid="entity-filter">
              <SelectValue placeholder="Entity Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="candidate">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#4CAF50]"></span>
                  Candidates
                </span>
              </SelectItem>
              <SelectItem value="contact">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#F9A825]"></span>
                  Contacts
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); setExpandedRow(null); }}>
            <SelectTrigger className="w-[160px]" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                  Pending
                </span>
              </SelectItem>
              <SelectItem value="complete">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  Complete
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleSearch} disabled={loading} className="btn-primary">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            Search
          </Button>
        </div>

        {/* Recipients Table */}
        <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : recipients.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="w-12"></TableHead>
                  <TableHead className="font-semibold">Recipient</TableHead>
                  <TableHead className="font-semibold">Type</TableHead>
                  <TableHead className="font-semibold">Documents Sent</TableHead>
                  <TableHead className="font-semibold">Outstanding</TableHead>
                  <TableHead className="font-semibold">Completed</TableHead>
                  <TableHead className="font-semibold">Last Activity</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recipients.map((recipient) => {
                  const entityType = recipient.entity_type || 'candidate';
                  const entityColor = ENTITY_COLORS[entityType] || FALLBACK_ENTITY_COLOR;
                  const totalCount = recipient.total_documents || 0;
                  const completedCount = recipient.completed_documents || 0;
                  const outstandingCount = recipient.pending_documents ?? (totalCount - completedCount);
                  const isComplete = totalCount > 0 && outstandingCount === 0;
                  const isExpanded = expandedRow === recipient.email;
                  // Hydrate the detail list once it's been fetched
                  const recipientWithDocs = {
                    ...recipient,
                    documents: recipientDetails[recipient.email] || [],
                  };
                  
                  return (
                    <>
                      <TableRow 
                        key={recipient.email}
                        className={`cursor-pointer transition-colors ${isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50/50'} ${isComplete ? 'bg-emerald-50/30' : ''}`}
                        onClick={() => toggleRow(recipient.email)}
                        data-testid={`recipient-row-${recipient.email}`}
                      >
                        <TableCell className="text-center">
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-muted-foreground mx-auto" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-muted-foreground mx-auto" />
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full ${entityColor.lightBg} flex items-center justify-center flex-shrink-0`}>
                              {entityType === 'contact' ? (
                                <Building2 className={`w-5 h-5 ${entityColor.iconColor}`} />
                              ) : (
                                <User className={`w-5 h-5 ${entityColor.iconColor}`} />
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium truncate">{recipient.name}</p>
                              <p className="text-sm text-muted-foreground truncate flex items-center gap-1">
                                <Mail className="w-3 h-3 flex-shrink-0" />
                                {recipient.email}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${entityColor.bg} ${entityColor.text}`}>
                            {entityColor.label}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="font-semibold text-lg">{recipient.total_documents}</span>
                        </TableCell>
                        <TableCell>
                          {outstandingCount > 0 ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                              <AlertCircle className="w-3.5 h-3.5" />
                              {outstandingCount}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              None
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <ProgressIndicator 
                            completed={completedCount} 
                            total={totalCount} 
                          />
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {recipient.last_activity 
                              ? formatDistanceToNow(new Date(recipient.last_activity), { addSuffix: true })
                              : '-'
                            }
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(getTenantPath(`/send/single?name=${encodeURIComponent(recipient.name)}&email=${encodeURIComponent(recipient.email)}&type=${entityType}`));
                              }}
                              className="border-primary/30 text-primary hover:bg-primary/5"
                              title="Send document to this recipient"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                            {entityType === 'candidate' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleResendLogin(recipient);
                                }}
                                disabled={resendingLogin === recipient.email}
                                className="border-orange-300 text-orange-700 hover:bg-orange-50"
                                title="Resend portal login details"
                                data-testid={`resend-login-${recipient.email}`}
                              >
                                {resendingLogin === recipient.email ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <KeyRound className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            {outstandingCount > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSendReminder(recipient);
                                }}
                                disabled={sendingReminder === recipient.email}
                                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                                title="Send reminder"
                              >
                                {sendingReminder === recipient.email ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Bell className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow key={`${recipient.email}-details`}>
                          <TableCell colSpan={8} className="p-0">
                            <RecipientDocumentsPanel 
                              recipient={recipientWithDocs} 
                              onSendReminder={handleSendReminder}
                              sendingReminder={sendingReminder}
                              onDocumentCancelled={() => {
                                setRecipientDetails((prev) => {
                                  const next = { ...prev };
                                  delete next[recipient.email];
                                  return next;
                                });
                                loadRecipients();
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-16">
              <User className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-2">No recipients found</h3>
              <p className="text-muted-foreground">
                {search || entityFilter !== 'all' || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filters' 
                  : 'Send documents to start tracking progress'
                }
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {((page - 1) * 25) + 1} - {Math.min(page * 25, totalCount)} of {totalCount} recipients
            </p>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => { setPage(p => Math.max(1, p - 1)); setExpandedRow(null); }}
                disabled={page === 1}
              >
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? "default" : "outline"}
                      size="sm"
                      className="w-9"
                      onClick={() => { setPage(pageNum); setExpandedRow(null); }}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => { setPage(p => Math.min(totalPages, p + 1)); setExpandedRow(null); }}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
  );

  // If embedded, return content directly without DashboardLayout
  if (embedded) {
    return content;
  }

  return (
    <DashboardLayout>
      {content}
    </DashboardLayout>
  );
}
